import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { clsx } from "clsx";
import { twRing } from "@/lib/emotionTw";

export type SliderSize = "xs" | "sm" | "md" | "lg";

export type SliderProps = Omit<InputHTMLAttributes<HTMLInputElement>, "defaultValue" | "max" | "min" | "onChange" | "size" | "step" | "type" | "value"> & {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  size?: SliderSize;
  formatValue?: (value: number) => ReactNode;
  containerClassName?: string;
  labelClassName?: string;
  valueClassName?: string;
  inputClassName?: string;
};

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, emitted in the original class-list order. Verified against the
 * frozen baseline build — do not "clean up" values. */

const SliderRoot = styled("div", {
  label: "AgenticSlider-root"
})({
  width: "100%"
});

const SliderHeader = styled("div", {
  label: "AgenticSlider-header"
})({
  marginBottom: ".5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: ".75rem"
});

const SliderLabel = styled("label", {
  label: "AgenticSlider-label",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { disabled: boolean } }>(({ ownerState }) => [
  {
    fontSize: "12px",
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.disabled && {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
]);

const sliderValueSize: Record<SliderSize, CSSObject> = {
  xs: { fontSize: "11px" },
  sm: { fontSize: ".75rem", lineHeight: "1rem" },
  md: { fontSize: ".75rem", lineHeight: "1rem" },
  lg: { fontSize: ".875rem", lineHeight: "1.25rem" }
};

const SliderValue = styled("output", {
  label: "AgenticSlider-value",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: SliderSize; disabled: boolean } }>(({ ownerState }) => [
  {
    flexShrink: 0,
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.disabled && {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  },
  sliderValueSize[ownerState.size]
]);

const sliderInputSize: Record<SliderSize, CSSObject> = {
  xs: { height: "1.25rem" },
  sm: { height: "1.5rem" },
  md: { height: "1.75rem" },
  lg: { height: "2rem" }
};

const SliderInput = styled("input", {
  label: "AgenticSlider-input",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: SliderSize } }>(({ ownerState }) => [
  {
    width: "100%",
    cursor: "pointer",
    accentColor: "hsl(var(--bfrs-color-primary) / 1)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    // ring-border-focus is a full-opacity ring color (--tw-ring-opacity: 1).
    "&:focus-visible": {
      ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
      "--tw-ring-opacity": "1"
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: ".5"
    }
  },
  sliderInputSize[ownerState.size]
]);

const SliderScale = styled("div", {
  label: "AgenticSlider-scale"
})({
  marginTop: ".25rem",
  display: "flex",
  justifyContent: "space-between",
  fontSize: "11px",
  fontWeight: 500,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
});

/* `bfrs-text-xs bfrs-leading-5`: both kept; `.bfrs-leading-5` sits later in the
 * compiled stylesheet than `.bfrs-text-xs`, so line-height 1.25rem wins. */
const sliderMessageBase: CSSObject = {
  marginTop: ".375rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem"
};

const SliderError = styled("p", {
  label: "AgenticSlider-error"
})({
  ...sliderMessageBase,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
});

const SliderHelper = styled("p", {
  label: "AgenticSlider-helper"
})({
  ...sliderMessageBase,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const getAriaValueText = (value: ReactNode) => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return undefined;
};

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      valueClassName,
      inputClassName,
      id,
      value,
      onValueChange,
      min = 0,
      max = 100,
      step = 1,
      disabled,
      label,
      helperText,
      errorText,
      size = "md",
      formatValue,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-valuetext": ariaValueText,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const errorId = errorText ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, helperId, errorId].filter(Boolean).join(" ") || undefined;
    const displayValue = formatValue ? formatValue(value) : value;

    return (
      <SliderRoot className={clsx(containerClassName, className) || undefined}>
        {(label || displayValue !== undefined) && (
          <SliderHeader>
            {label ? (
              <SliderLabel htmlFor={inputId} ownerState={{ disabled: Boolean(disabled) }} className={labelClassName}>
                {label}
              </SliderLabel>
            ) : (
              <span />
            )}
            <SliderValue htmlFor={inputId} ownerState={{ size, disabled: Boolean(disabled) }} className={valueClassName}>
              {displayValue}
            </SliderValue>
          </SliderHeader>
        )}
        <SliderInput
          ref={ref}
          id={inputId}
          type="range"
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid ?? Boolean(errorText)}
          aria-valuetext={ariaValueText ?? getAriaValueText(displayValue)}
          ownerState={{ size }}
          className={inputClassName}
          onChange={(event) => onValueChange(event.currentTarget.valueAsNumber)}
          {...props}
        />
        <SliderScale>
          <span>{min}</span>
          <span>{max}</span>
        </SliderScale>
        {errorText ? (
          <SliderError id={errorId}>{errorText}</SliderError>
        ) : helperText ? (
          <SliderHelper id={helperId}>{helperText}</SliderHelper>
        ) : null}
      </SliderRoot>
    );
  }
);

Slider.displayName = "Slider";
